// API クライアント
// 設計書 §3.2 共通レスポンス形式に従い、ApiSuccessResponse / ApiErrorResponse を扱う。
// 認証は localStorage の access_token を Authorization: Bearer ヘッダで自動付与。
//
// 401 を受けた場合は一度だけ silent refresh を試みる(設計書 §3.4.1
// /v1/auth/refresh / §3.5.1 /v1/admin/auth/refresh)。同時に複数リクエストが
// 401 に当たっても refresh は scope ごとに 1 回しか走らないよう Promise キャッシュ
// で排他する。refresh も失敗した場合は localStorage を消し、`auth-events` で
// 「セッション期限切れ」を React 側に通知して /login or /admin/login へ遷移させる。

import type {
  ApiErrorResponse,
  ApiSuccessResponse,
  ErrorCode,
} from '@safelens/shared-types';
import {
  emitAuthExpired,
  setExpiredFlash,
  type AuthScope,
} from '../lib/auth-events';
import { STORAGE_KEYS } from '../lib/storage';

const API_BASE = '/v1';

export class ApiClientError extends Error {
  public readonly status: number;
  public readonly code: ErrorCode | string;
  public readonly details?: Record<string, unknown> | undefined;

  constructor(
    status: number,
    code: ErrorCode | string,
    message: string,
    details?: Record<string, unknown>,
  ) {
    super(message);
    this.name = 'ApiClientError';
    this.status = status;
    this.code = code;
    if (details !== undefined) {
      this.details = details;
    }
  }
}

interface ApiRequestInit {
  method?: 'GET' | 'POST' | 'PATCH' | 'DELETE' | 'PUT';
  body?: object | undefined;
  headers?: Record<string, string>;
  // signal などは必要になったら追加
  // 内部用: silent refresh から呼ばれた retry を識別する。直接指定しないこと。
  _retry?: boolean;
}

// admin 系 (`/admin/...`) と performer 系で別の access_token を使う。
// パスから自動判定して該当の localStorage キーを引く。
function pickAccessTokenKey(path: string): string {
  return path.startsWith('/admin/')
    ? STORAGE_KEYS.ADMIN_ACCESS_TOKEN
    : STORAGE_KEYS.ACCESS_TOKEN;
}

function pickScope(path: string): AuthScope {
  return path.startsWith('/admin/') ? 'admin' : 'performer';
}

function pickRefreshTokenKey(scope: AuthScope): string {
  return scope === 'admin'
    ? STORAGE_KEYS.ADMIN_REFRESH_TOKEN
    : STORAGE_KEYS.REFRESH_TOKEN;
}

function pickRefreshEndpoint(scope: AuthScope): string {
  return scope === 'admin' ? '/admin/auth/refresh' : '/auth/refresh';
}

interface RefreshResponse {
  access_token: string;
  refresh_token: string;
  expires_in: number;
  refresh_expires_in: number;
}

// 同 scope で並行する 401 をまとめるための Promise キャッシュ。
// 値が入っている間は新しい refresh を発火させず、既存の Promise を await する。
const refreshInFlight: Record<AuthScope, Promise<string | null> | null> = {
  performer: null,
  admin: null,
};

// access token を取りに行く(scope に応じた refresh エンドポイントを叩いて
// localStorage を更新)。失敗したら null を返し、トークンをクリアする。
async function performRefresh(scope: AuthScope): Promise<string | null> {
  const refreshTokenKey = pickRefreshTokenKey(scope);
  const refreshToken = localStorage.getItem(refreshTokenKey);
  if (!refreshToken) return null;

  const endpoint = pickRefreshEndpoint(scope);
  let res: Response;
  try {
    res = await fetch(API_BASE + endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token: refreshToken }),
    });
  } catch {
    // ネットワーク障害は「現状の token をクリアしない」方針で false を返す
    // (画面側で「未認証」状態に倒すかは scope expired 通知次第)
    return null;
  }

  if (!res.ok) {
    // refresh が無効/期限切れの場合は localStorage をクリアして失敗扱い
    return null;
  }

  let body: ApiSuccessResponse<RefreshResponse> | null = null;
  try {
    body = (await res.json()) as ApiSuccessResponse<RefreshResponse>;
  } catch {
    return null;
  }
  const data = body?.data;
  if (!data || typeof data.access_token !== 'string' || typeof data.refresh_token !== 'string') {
    return null;
  }

  // refresh token は rotation されるので必ず保存し直す(設計書 §3.3)
  const accessTokenKey =
    scope === 'admin' ? STORAGE_KEYS.ADMIN_ACCESS_TOKEN : STORAGE_KEYS.ACCESS_TOKEN;
  localStorage.setItem(accessTokenKey, data.access_token);
  localStorage.setItem(refreshTokenKey, data.refresh_token);
  return data.access_token;
}

// 排他付きの refresh エントリーポイント。同時に複数 caller が呼んでも
// 内部の fetch は 1 回しか走らない。
async function refreshAccessToken(scope: AuthScope): Promise<string | null> {
  const existing = refreshInFlight[scope];
  if (existing) return existing;
  const p = (async () => {
    try {
      return await performRefresh(scope);
    } finally {
      refreshInFlight[scope] = null;
    }
  })();
  refreshInFlight[scope] = p;
  return p;
}

// 外部から呼びたいケース用(AuthContext の heartbeat タイマーなど)。
// 結果は「成功したら新しい access token、失敗したら null」。
// 失敗時の logout 遷移呼び出しは呼び出し側の責務(heartbeat 経由か apiRequest
// 経由かで挙動を切り分けたいため)。
export function refreshSession(scope: AuthScope): Promise<string | null> {
  return refreshAccessToken(scope);
}

// scope のセッションを完全にクリアして React 側に「期限切れ」を通知する。
// React 側はこれを受けて logout() → navigate('/login') する。
// sessionStorage にもフラッシュを書き込み、リダイレクト先のログイン画面で
// トースト「セッションの有効期限が切れました」を 1 度だけ表示する。
export function expireSession(scope: AuthScope): void {
  if (scope === 'admin') {
    localStorage.removeItem(STORAGE_KEYS.ADMIN_ACCESS_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.ADMIN_REFRESH_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.ADMIN);
  } else {
    localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.PERFORMER);
  }
  setExpiredFlash(scope);
  emitAuthExpired(scope);
}

// 生のレスポンス本体(未パース版)
// `{data: T}` だけでなく `{data, pagination}` のように同階層に追加フィールドを
// 持つレスポンス用。トップレベル全体を呼び出し側で受け取る。
export async function apiRequestRaw<TEnvelope>(
  path: string,
  init: ApiRequestInit = {},
): Promise<TEnvelope> {
  const accessTokenKey = pickAccessTokenKey(path);
  const accessToken = localStorage.getItem(accessTokenKey);
  const headers = new Headers(init.headers);
  if (init.body !== undefined && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }
  if (accessToken && !headers.has('Authorization')) {
    headers.set('Authorization', `Bearer ${accessToken}`);
  }

  const res = await fetch(API_BASE + path, {
    method: init.method ?? 'GET',
    headers,
    body: init.body !== undefined ? JSON.stringify(init.body) : undefined,
  });

  // 401 を受けたら一度だけ silent refresh を試みる。
  // - retry フラグが立っている = 既に refresh 済みでもう一度 401 → 諦めてログアウト遷移
  // - refresh / logout 自体の 401 はループ防止のため対象外(path で除外)
  // - access token を持っていない状態 (= 未ログイン)で 401 が返ったケースは
  //   refresh しても無意味。トークンの有無で分岐する。
  const isAuthEndpoint =
    path.startsWith('/auth/') || path.startsWith('/admin/auth/');
  if (res.status === 401 && !init._retry && !isAuthEndpoint) {
    const scope = pickScope(path);
    const refreshTokenKey = pickRefreshTokenKey(scope);
    const refreshToken = localStorage.getItem(refreshTokenKey);
    if (refreshToken) {
      const newAccess = await refreshAccessToken(scope);
      if (newAccess) {
        return apiRequestRaw<TEnvelope>(path, { ...init, _retry: true });
      }
      // refresh も失敗 → セッション期限切れとしてログアウト遷移
      expireSession(scope);
    } else if (accessToken) {
      // refresh token が無いのに access token で 401
      // → ログアウト遷移(状態を整合させる)
      expireSession(scope);
    }
  }

  // retry したのにまた 401 → セッション期限切れ確定
  if (res.status === 401 && init._retry && !isAuthEndpoint) {
    const scope = pickScope(path);
    expireSession(scope);
  }

  if (res.status === 204) {
    return undefined as TEnvelope;
  }

  const text = await res.text();
  let payload: unknown = null;
  if (text) {
    try {
      payload = JSON.parse(text);
    } catch {
      // non-JSON 応答は下で 5xx 扱い
    }
  }

  if (!res.ok) {
    const errBody = (payload as ApiErrorResponse | null)?.error;
    throw new ApiClientError(
      res.status,
      errBody?.code ?? 'UNKNOWN',
      errBody?.message ?? `HTTP ${res.status}`,
      errBody?.details,
    );
  }

  return payload as TEnvelope;
}

export async function apiRequest<T>(path: string, init: ApiRequestInit = {}): Promise<T> {
  const envelope = await apiRequestRaw<ApiSuccessResponse<T> | null>(path, init);
  return envelope?.data as T;
}
